import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { UsersApiService } from '../http/services/user.service';
import { inject } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, catchError, finalize, take, switchMap } from 'rxjs/operators';
import { UserService } from '../services/user.service';
import { PostHogService } from '../../shared/services/post-hog.service';
import { firebaseAuth } from '../firebase';
import { AssetsApiService } from '../http/services/assets.service';
import { environment } from 'src/environments/environment';

function decodeJwtPayload(token: string): any {
  try {
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload));
  } catch (error) {
    console.error('Invalid token format', error);
    return null;
  }
}

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> => {
  const router = inject(Router);
  const userApiService = inject(UsersApiService);
  const userService = inject(UserService);
  const postHogService = inject(PostHogService);
  const assetsApiService = inject(AssetsApiService);

  const email = localStorage.getItem('tif-user-email') || '';
  const token = localStorage.getItem('tif-access-token');
  const photo = localStorage.getItem('tif-user-photo');

  // The current route being accessed
  const currentRoute = state.url;

  // Allow access to "dashboard/onboarding" only if token exists
  if (currentRoute === '/dashboard/onboarding') {
    if (token) {
      if (email) {
        postHogService.identify(email);
      }
      return of(true);
    } else {
      postHogService.reset();
      router.navigate(['/login']);
      return of(false);
    }
  }

  // If token is missing for other routes, block access
  if (!token) {
    postHogService.reset();
    router.navigate(['/login']);
    return of(false);
  }

  const proceedWithUserCheck = (): Observable<boolean> => {
    // For other routes, continue with user authentication check
    return userApiService.getUserByEmail(email).pipe(
      take(1), // Complete after first emission
      map((response) => {
        const { firstName, lastName, email, phone, jobTitle, timezone, language, industry, organization, status, roles } = response.data.user;
        // Check if user is approved
        if (status !== 'APPROVED') {
          postHogService.reset();
          router.navigate(['/login']);
          return false;
        }

        // Create user object for JWT
        const userForToken = {
          firstName,
          lastName,
          email,
          phone,
          jobTitle,
          timezone,
          language,
          industry,
          organization: organization ? {
            id: organization.id,
            name: organization.name,
            subscription: organization.subscription
          } : null
        };

        // Store encoded user data
        const encodedUser = btoa(JSON.stringify(userForToken));
        localStorage.setItem('tif-user-data', encodedUser);

        userService.setState({ firstName, lastName, email, phone, jobTitle, timezone, language, organization, photo, roles, industry });

        postHogService.identify(email);

        // Initialize default AI model if not set (non-blocking)
        initializeDefaultAIModel(assetsApiService);

        // Move subscription plan check to a separate subscription to avoid blocking navigation
        if (organization?.subscription?.plan) {
          userApiService.getPlanById(organization.subscription.plan.id)
            .pipe(take(1))
            .subscribe((response) => {
              const { name, description, stripePlanId, rate, featureQuotaMapping } = response.data.plan;
              userService.setState({ plan: { name, description, stripePlanId, rate, featureQuotaMapping } });
            });
        }

        return true;
      }),
      catchError(() => {
        postHogService.reset();
        router.navigate(['/login']);
        return of(false);
      }),
      finalize(() => {
        // cleanup if needed
      })
    );
  };

  // 🔐 Check for token refresh need
  const decoded = decodeJwtPayload(token);
  const currentTimeInSec = Math.floor(Date.now() / 1000);
  const expiresIn = decoded?.exp - currentTimeInSec;

  if (expiresIn <= 600) { // If token expires in 10 min or less
    console.log('[AuthGuard] Access token is nearing expiration. Attempting refresh...');
    const currentUser = firebaseAuth.currentUser;

    if (currentUser) {
      return from(currentUser.getIdToken(true)).pipe(
        map((freshToken) => {
          localStorage.setItem('tif-access-token', freshToken);
          console.log('[AuthGuard] Access token successfully refreshed.');

          if (email) {
            postHogService.identify(email);
          }
          return true;
        }),
        switchMap(() => proceedWithUserCheck()),
        catchError(() => {
          postHogService.reset();
          router.navigate(['/login']);
          return of(false);
        })
      );
    }
  }

  // No refresh needed — just proceed
  return proceedWithUserCheck();
};

// Helper function to initialize default AI model
export function initializeDefaultAIModel(assetsApiService: AssetsApiService): void {
  const storedModel = localStorage.getItem('AI_MODEL');
  
  // Check if AI_MODEL is already set and valid
  if (storedModel) {
    try {
      const parsed = JSON.parse(storedModel);
      // If it's a valid model object with an id, we're good
      if (parsed && parsed.id) {
        return;
      }
    } catch (e) {
      // Invalid JSON, continue to set default
    }
  }

  // If no valid model is set, fetch the default model by asset ID
  assetsApiService.getAssetById(environment.defaultModelId).subscribe({
    next: (response) => {
      if (response?.data?.asset) {
        const defaultModel = {
          id: response.data.asset.id,
          key: response.data.asset.parameters?.AI_MODEL || '',
          name: response.data.asset.name,
          logoUrl: response.data.asset.launchURL || '',
        };
        localStorage.setItem('AI_MODEL', JSON.stringify(defaultModel));
      }
    },
    error: (error) => {
      console.error('Error initializing default AI model:', error);
      // Don't block app initialization if this fails
    }
  });
}